import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../static/styles/WeeklyTournamentJoin.module.css';
import calendarIcon from '../static/img/Home/calendarIcon.svg';
import weeklyTournamentBg from '../static/img/WeeklyTournaments/imagePubgWeeklyTournament.avif';

// Import API function
import { fetchTournamentById, tournamentEntry } from '../api/tournaments';
import MessagePopup from '../components/MessagePopup';

const WeeklyTournamentJoin = () => {
    const [searchParams] = useSearchParams();
    const tournamentId = searchParams.get('tournamentId');
    const [tournamentData, setTournamentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEnded, setIsEnded] = useState(false);
    const [makeEntryLoading, setMakeEntryLoading] = useState(false);
    const [entryError, setEntryError] = useState(null);
    const [entrySuccess, setEntrySuccess] = useState(null);
    const [popupMessage, setPopupMessage] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchTournamentDetails = async () => {
            if (!tournamentId) {
                setError('Tournament ID is missing');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const data = await fetchTournamentById(tournamentId);
                setTournamentData(data);
                setIsEnded(data.status > 2);
                setError(null);
            } catch (err) {
                setError(err.message || 'Failed to load tournament details');
            } finally {
                setLoading(false);
            }
        };

        fetchTournamentDetails();
    }, [tournamentId]);

    useEffect(() => {
        document.title = "Join Tournament | Ufone ESG"
    }, [])

    const handleRegister = async () => {
        if (makeEntryLoading) return;

        setMakeEntryLoading(true);
        setEntryError(null);
        setEntrySuccess(null);

        const uidInput = document.querySelector('input[placeholder="Enter Your UID"]');
        const nickInput = document.querySelector('input[placeholder="Enter Your NICK"]');
        const emailInput = document.querySelector('input[placeholder="Enter Your Email"]');

        let hasError = false;

        if (tournamentData.UIDCheck === 1 && (!uidInput || !uidInput.value.trim())) {
            uidInput.classList.add(styles.errorInput);
            hasError = true;
        } else if (uidInput) {
            uidInput.classList.remove(styles.errorInput);
        }

        if (!nickInput || !nickInput.value.trim()) {
            nickInput.classList.add(styles.errorInput);
            hasError = true;
        } else {
            nickInput.classList.remove(styles.errorInput);
        }

        if (tournamentData.tourn_type !== 'Credits' && (!emailInput || !emailInput.value.trim())) {
            emailInput.classList.add(styles.errorInput);
            hasError = true;
        } else if (emailInput) {
            emailInput.classList.remove(styles.errorInput);
        }

        if (hasError) {
            setMakeEntryLoading(false);
            return;
        }

        const params = {
            id: tournamentId,
            pubg_id: uidInput ? uidInput.value.trim() : undefined,
            pubg_nick: nickInput.value.trim(),
            userEmail: emailInput ? emailInput.value.trim() : undefined,
        };

        try {
            const response = await tournamentEntry(params);
            if (response?.data?.already_submited) {
                setPopupMessage("You have already submitted. You will receive an SMS with tournament room ID and room password.");
            } else {
                setPopupMessage("You are successfully registered. You will receive an SMS with tournament room ID and room password");
            }
        } catch (error) {
            setPopupMessage(error.message || "Failed to register for the tournament.");
        } finally {
            setMakeEntryLoading(false);
            setIsPopupOpen(true);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading tournament details...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    if (!tournamentData) {
        return <div className={styles.error}>No tournament data available</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.mainImageContainer}>
                <div
                    className={`${styles.mainImage} ${isEnded ? styles.endedImage : ''}`}
                    style={{
                        '--bg-image': `url(${tournamentData.banner || weeklyTournamentBg})`,
                        backgroundImage: `url(${tournamentData.banner || weeklyTournamentBg})`
                    }}
                >
                    {isEnded && (
                        <div className={styles.highlightsOverlay}>
                            <p>VIEW HIGHLIGHTS</p>
                        </div>
                    )}
                    {tournamentData.status === 2 && (
                        <div className={styles.highlightsOverlay}>
                            <p>Tournament is in progress</p>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.joinSection}>
                <div className={styles.joinBackground} style={{ '--background-image': `url(${tournamentData.banner || weeklyTournamentBg})` }}>
                    <div className={styles.overlay}>
                        <div className={styles.topContent}>
                            <div className={styles.prizeInfo}>
                                <h3>Entry Fee: {tournamentData.entryFee ? `${tournamentData.entryFee} Credits` : 'Free Entry'}</h3>
                                <div className={styles.prizes}>
                                    {tournamentData.prize && <p>1st: {tournamentData.prize} Credits</p>}
                                    {tournamentData.second_prize && <p>2nd: {tournamentData.second_prize} Credits</p>}
                                    {tournamentData.third_prize && <p>3rd: {tournamentData.third_prize} Credits</p>}
                                </div>
                            </div>
                            <div className={styles.participants}>
                                <p>( Participants: {tournamentData.entries?.length || 0} )</p>
                            </div>

                            {/* {tournamentData.status === 2 && (
                                <p className="detail mb-4">
                                </p>
                            )} */}
                        </div>
                        <div className={styles.bottomContent}>
                            <div>
                                <p>{tournamentData.tourn_name}</p>
                                <p>{tournamentData.platform}</p>
                            </div>
                            <div className={styles.timeInfo}>
                                <div className={styles.startTime}>
                                    <img src={calendarIcon} alt="Calendar" />
                                    <div>
                                        <p>Starting At</p>
                                        <p>{tournamentData.timeStart?.split(' ')[0]}</p>
                                        <p>{tournamentData.timeStart?.split(' ').slice(1).join(' ')}</p>
                                    </div>
                                </div>
                                <div className={styles.endTime}>
                                    <img src={calendarIcon} alt="Calendar" />
                                    <div>
                                        <p>Ending At</p>
                                        <p>{tournamentData.timeEnd?.split(' ')[0]}</p>
                                        <p>{tournamentData.timeEnd?.split(' ').slice(1).join(' ')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.formSection}>
                    {tournamentData.UIDCheck === 1 && (
                        <input type="text" placeholder="Enter Your UID" className={styles.input} disabled={tournamentData.status >= 2} />
                    )}
                    <input type="text" placeholder="Enter Your NICK" className={styles.input} disabled={tournamentData.status >= 2} />
                    {tournamentData.tourn_type !== 'Credits' && <input type="email" placeholder="Enter Your Email" className={styles.input} disabled={tournamentData.status >= 2} />}
                    <button
                        className={styles.registerButton}
                        onClick={handleRegister}
                        disabled={makeEntryLoading || isEnded || tournamentData.status === 2}
                    >
                        {makeEntryLoading ? "Please wait..." : tournamentData.status >= 2 ? "Registrations Closed" : "Register"}
                    </button>
                </div>
            </div>
            <MessagePopup
                isOpen={isPopupOpen}
                message={popupMessage}
                onClose={() => {setIsPopupOpen(false); navigate('/')}}
            />
        </div>
    );
};

export default WeeklyTournamentJoin;